/**
 * Autosave scheduler — PURE module (no React, no Lexical, no fetch), fully
 * unit-testable with fake timers.
 *
 * The load-bearing design decision (approved 2026-07-09): the scheduler
 * NEVER holds a copy of the document. It holds only a dirty flag; the
 * document is read via getDocument() at SEND time. A failed save therefore
 * cannot strand a stale queued copy — every retry, however delayed, sends
 * whatever the writer has typed by the moment it fires.
 *
 * Rules:
 *  - markDirty() (called per editor change) restarts the debounce timer
 *  - one save in flight at a time; typing during a save re-dirties and a
 *    follow-up save is scheduled after it settles
 *  - TRANSIENT failure (network, 5xx, 408, 429) → exponential backoff retry
 *    (still reading latest at fire time); success resets the backoff
 *  - PERMANENT failure (other 4xx: 401 session dead, 409 conflict, 413/422
 *    rejects) → NO retry loop; a terminal 'error' status with
 *    permanent: true is emitted so the UI can be honest ("not saving").
 *    The dirty flag survives — the next edit schedules a fresh attempt.
 *  - flush() saves NOW: it cancels timers, WAITS OUT any in-flight save,
 *    and then saves again if anything is still dirty — content typed during
 *    an in-flight save is never stranded (unmount / tab-hide / page-close)
 *  - getDocument() may return null to signal "nothing serializable right
 *    now" — the save is skipped and the dirty flag survives
 *
 * Status callbacks: 'dirty' | 'saving' | 'saved' | 'error' (± permanent).
 */

export const AUTOSAVE_DEBOUNCE_MS = 2500;
export const AUTOSAVE_MAX_BACKOFF_MS = 30_000;

export function backoffDelay(attempt, base = AUTOSAVE_DEBOUNCE_MS, cap = AUTOSAVE_MAX_BACKOFF_MS) {
  return Math.min(base * 2 ** attempt, cap);
}

/**
 * Retryable = we have any reason to believe trying again could work.
 * A 4xx (except timeout/rate-limit) is a deterministic reject — retrying
 * the identical payload forever behind "retrying…" would be lying to the
 * writer while nothing persists.
 */
export function isPermanentSaveError(error) {
  const status = error?.status;
  if (typeof status !== 'number') return false; // network errors are transient
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

/**
 * Which Lexical updates count as content changes worth saving. Pure so the
 * suppression rules are pinned by unit tests:
 *  - selection-only updates carry empty dirty sets → no save
 *  - the INITIAL state set is tagged 'history-merge' → no save (opening a
 *    script must never phantom-save: it would burn a V2b snapshot slot per
 *    open and persist load-time mutations)
 *  - undo/redo ('historic' tag) DOES count — undone content must save
 */
export function shouldMarkDirty(dirtyElements, dirtyLeaves, tags) {
  if (tags.has('history-merge')) return false;
  return dirtyElements.size > 0 || dirtyLeaves.size > 0;
}

export function createAutosaver({
  getDocument,
  save,
  onStatus = () => {},
  debounceMs = AUTOSAVE_DEBOUNCE_MS,
  maxBackoffMs = AUTOSAVE_MAX_BACKOFF_MS,
}) {
  let dirty = false;
  let inFlight = null; // Promise while a save is running, else null
  let failedAttempts = 0;
  let timer = null;
  let destroyed = false;

  const currentBackoff = () => backoffDelay(failedAttempts - 1, debounceMs, maxBackoffMs);

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const schedule = (delay) => {
    clearTimer();
    timer = setTimeout(runSave, delay);
  };

  async function runSave(meta) {
    clearTimer(); // direct calls (flush) must kill any pending timer too
    if (destroyed || inFlight || !dirty) return undefined;
    // Read the document NOW — never earlier. This is the property that
    // makes retries safe: everything typed during failed attempts is in
    // this read.
    const doc = getDocument();
    if (doc == null) return undefined; // stay dirty, next markDirty reschedules
    // Anything typed AFTER this read re-dirties and triggers a follow-up.
    dirty = false;
    onStatus({ state: 'saving' });
    inFlight = (async () => {
      try {
        await save(doc, meta);
        failedAttempts = 0;
        if (destroyed) return;
        if (dirty) {
          schedule(debounceMs); // typed during the save — go again
        } else {
          onStatus({ state: 'saved', at: Date.now() });
        }
      } catch (error) {
        dirty = true; // the write didn't land; the content is still unsaved
        if (destroyed) return;
        if (isPermanentSaveError(error)) {
          // Deterministic reject: retrying the same payload cannot succeed.
          // Be honest instead of looping. The next edit re-attempts (the
          // writer may have fixed the cause, e.g. deleted oversized text
          // or logged back in).
          failedAttempts = 0;
          onStatus({ state: 'error', error, permanent: true });
        } else {
          failedAttempts += 1;
          const delay = currentBackoff();
          onStatus({ state: 'error', error, retryInMs: delay });
          schedule(delay);
        }
      } finally {
        inFlight = null;
      }
    })();
    return inFlight;
  }

  return {
    /** Editor content changed. Restarts the debounce clock. */
    markDirty() {
      if (destroyed) return;
      dirty = true;
      onStatus({ state: 'dirty' });
      // A backoff retry already queued keeps its slot (it reads latest
      // anyway); fresh typing only restarts the short debounce when no
      // failure backoff is pending.
      if (!inFlight && failedAttempts === 0) schedule(debounceMs);
      else if (!inFlight && timer === null) schedule(currentBackoff());
    },

    /**
     * Save NOW (unmount, tab hide, page close). Waits out an in-flight
     * save, then saves again if anything is still unsaved — nothing typed
     * during the in-flight request is stranded. `meta` is forwarded to
     * save(doc, meta) (e.g. { unload: true } for keepalive decisions).
     */
    async flush(meta) {
      if (destroyed) return;
      clearTimer();
      if (inFlight) {
        try {
          await inFlight;
        } catch {
          // runSave handled status/dirty; fall through to re-check.
        }
      }
      if (dirty && !destroyed) await runSave(meta);
    },

    /** True when content exists that has not landed on the server. */
    isDirty() {
      return dirty || inFlight !== null;
    },

    destroy() {
      destroyed = true;
      clearTimer();
    },
  };
}
